import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useDraggablePanel } from '../utils/panelDragUtils';
import { EssenceConsumption } from './EssenceConsumption';
import { useGlobalTooltip } from './GlobalTooltip';
import { ItemIcon } from '../utils/itemIconRenderer';

// Inject shimmer animation styles
const injectShimmerStyles = (): void => {
  const styleId = 'inventory-shimmer-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes inventoryShimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

// Skeleton loader component
interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
}

const Skeleton: React.FC<SkeletonProps> = ({ width = '100%', height = 16, style }) => (
  <div style={{
    width,
    height,
    background: 'linear-gradient(90deg, #1f2937 25%, #374151 50%, #1f2937 75%)',
    backgroundSize: '200% 100%',
    animation: 'inventoryShimmer 1.5s ease-in-out infinite',
    ...style,
  }} />
);

// Inventory slot skeleton
const SlotSkeleton: React.FC = () => (
  <div className="inventory-slot" style={{ borderColor: '#30363d' }}>
    <Skeleton width={32} height={32} />
  </div>
);

// Equipment slot skeleton
const EquipSlotSkeleton: React.FC<{ label: string }> = ({ label }) => (
  <div className="equip-slot-container">
    <div className="equip-slot-label">{label}</div>
    <div className="equip-slot" style={{ borderColor: '#30363d' }}>
      <Skeleton width={28} height={28} />
    </div>
  </div>
);

// ============ TYPES ============

interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  type: 'weapon' | 'armor' | 'accessory' | 'consumable' | 'material' | 'blueprint';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  equipSlot?: string;
  stats?: Record<string, number>;
  buyPrice: number;
  sellPrice: number;
  icon: string;
}

interface InventoryItem {
  itemId: string;
  quantity: number;
  definition: ItemDefinition | null;
}

interface CourierPackageInfo {
  id: string;
  name: string;
  destinationLocationId: string;
  destinationLocationName: string;
  destinationX: number;
  destinationY: number;
  rewardPieces: number;
  timeLimitExpiresAt: number | null;
}

const isCourierPackage = (itemId: string): boolean => {
  return itemId.startsWith('courier_package_') || itemId.startsWith('regulated_package_');
};

interface Equipment {
  weapon: ItemDefinition | null;
  armor: ItemDefinition | null;
  accessory1: ItemDefinition | null;
  accessory2: ItemDefinition | null;
}

interface InventoryPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  pieces: number;
  onInventoryUpdate?: (items: InventoryItem[]) => void;
  onOpenWildcardPowers?: () => void;
  hasEssence?: boolean;
}

// ============ RARITY COLORS & LABELS ============

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const RARITY_LABELS: Record<string, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
};

const isContrabandItemId = (itemId: string | undefined): boolean => {
  if (!itemId) return false;
  return itemId.includes('contraband');
};

const getIconSrc = (icon: string | undefined, itemId: string | undefined): string => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  if (isContrabandItemId(itemId)) {
    return `${publicUrl}/assets/portraits/generic_contraband.png`;
  }
  if (icon && icon.startsWith('/assets')) {
    return `${publicUrl}${icon}`;
  }
  if (icon && (icon.startsWith('http://') || icon.startsWith('https://'))) {
    return icon;
  }
  // Use blueprint fallback for blueprint items
  if (itemId && itemId.startsWith('blueprint_')) {
    return `${publicUrl}/assets/items/blueprints/blueprint_fallback.png`;
  }
  return `${publicUrl}/assets/unknown.png`;
};

const SLOT_LABELS: Record<string, string> = {
  weapon: 'Weapon',
  armor: 'Armor',
  accessory1: 'Accessory',
  accessory2: 'Accessory',
};

const STAT_LABELS: Record<string, string> = {
  attackBonus: 'Attack',
  defenseBonus: 'Defense',
  critChance: 'Crit %',
  dodgeChance: 'Dodge %',
  speedBonus: 'Speed',
  hpBonus: 'HP',
};

// Helper to render item icon with fallback placeholder
const renderItemIcon = (icon: string | undefined, itemId: string | undefined, name: string, className: string = 'item-img'): JSX.Element => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  
  // Determine fallback chain based on item type
  const getFallbackChain = (): string[] => {
    const chain: string[] = [];
    
    if (isContrabandItemId(itemId)) {
      chain.push(`${publicUrl}/assets/portraits/generic_contraband.png`);
    }
    
    if (itemId && itemId.startsWith('blueprint_')) {
      chain.push(`${publicUrl}/assets/items/blueprints/blueprint_fallback.png`);
    }
    
    // Universal fallback
    chain.push(`${publicUrl}/assets/unknown.png`);
    
    return chain;
  };
  
  const fallbackChain = getFallbackChain();
  let currentFallbackIndex = 0;
  
  return (
    <img
      src={getIconSrc(icon, itemId)}
      alt={name}
      className={className}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        currentFallbackIndex++;
        
        // Try next fallback in chain
        if (currentFallbackIndex < fallbackChain.length) {
          const nextFallback = fallbackChain[currentFallbackIndex];
          if (target.src !== nextFallback) {
            target.src = nextFallback;
            return;
          }
        }
        
        // All fallbacks exhausted, stop trying
        target.onerror = null;
      }}
    />
  );
};

// ============ COMPONENT ============

// Context menu state
interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  item: InventoryItem | null;
}


export const InventoryPanel: React.FC<InventoryPanelProps> = ({ socket, isOpen, onClose, pieces, onInventoryUpdate, onOpenWildcardPowers, hasEssence }) => {
  const { showTooltip, hideTooltip } = useGlobalTooltip();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [equipment, setEquipment] = useState<Equipment>({
    weapon: null,
    armor: null,
    accessory1: null,
    accessory2: null,
  });
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [message, setMessage] = useState<string>('');
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({ visible: false, x: 0, y: 0, item: null });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [draggedItem, setDraggedItem] = useState<InventoryItem | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [courierPackageInfo, setCourierPackageInfo] = useState<CourierPackageInfo | null>(null);
  const [essenceConsumption, setEssenceConsumption] = useState<{ isOpen: boolean; itemId: string; name: string; description: string; rarity: 'rare' | 'epic' | 'legendary'; unlocksMove: string; moveDefinition: any } | null>(null);
  const { handleHeaderMouseDown, style: dragStyle, panelRef } = useDraggablePanel(50, 50, {
    storageKey: 'panel:inventory',
    clampToViewport: true,
  });

  // Inject shimmer styles on mount
  useEffect(() => {
    injectShimmerStyles();
  }, []);
  
  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClick = (): void => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, item: null });
      }
    };
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, [contextMenu.visible]);

  // Request inventory on open
  useEffect(() => {
    if (isOpen && socket) {
      setIsLoading(true);
      console.log('📦 Emitting inventory:get request, socket.connected:', socket.connected);
      socket.emit('inventory:get');
    }
  }, [isOpen, socket]);

  // Socket listeners
  useEffect(() => {
    if (!socket) {
      console.log('📦 [InventoryPanel] No socket available');
      return;
    }
    console.log('📦 [InventoryPanel] Setting up socket listeners');

    const handleInventoryUpdate = (data: any): void => {
      // Server now sends enriched inventory with definitions already included
      // Structure: { playerId, items: [{itemId, quantity, definition}], maxSlots, equipment: {weapon, armor, accessory1, accessory2} }
      console.log('📦 Received inventory:data event', { itemCount: data?.items?.length, hasEquipment: !!data?.equipment });
      const items: InventoryItem[] = (data.items || []).map((item: any) => ({
        itemId: item.itemId,
        quantity: item.quantity,
        definition: item.definition || null,
      }));

      // Equipment slots already have definitions from server
      const equipment: Equipment = {
        weapon: data.equipment?.weapon || null,
        armor: data.equipment?.armor || null,
        accessory1: data.equipment?.accessory1 || null,
        accessory2: data.equipment?.accessory2 || null,
      };

      setItems(items);
      setEquipment(equipment);
      setIsLoading(false);
      onInventoryUpdate?.(items);
    };

    const handleSellResult = (data: { success: boolean; message: string }): void => {
      setMessage(data.message);
      if (data.success) {
        socket.emit('inventory:get');
      }
      setTimeout(() => setMessage(''), 3000);
    };

    const handleEquipResult = (data: { success: boolean; message: string }): void => {
      setMessage(data.message);
      setTimeout(() => setMessage(''), 3000);
    };

    const handleError = (data: { message: string }): void => {
      setMessage('Error: ' + data.message);
      setTimeout(() => setMessage(''), 3000);
    };

    const handleLootItems = (data: { items: Array<{ itemId: string; quantity: number; item: ItemDefinition }> }): void => {
      const lootMsg = data.items.map(i => '+' + i.quantity + ' ' + (i.item?.name || i.itemId)).join(', ');
      setMessage('Loot: ' + lootMsg);
      setTimeout(() => setMessage(''), 5000);
      socket.emit('inventory:get');
    };

    const handleUseResult = (data: { success: boolean; message: string }): void => {
      setMessage(data.message);
      if (data.success) {
        // Note: Healing sounds will be played via ship:effect event, but item use sound for non-healing items
        socket.emit('inventory:get');
        setSelectedItem(null);
        setContextMenu({ visible: false, x: 0, y: 0, item: null });
      }
      setTimeout(() => setMessage(''), 3000);
    };

    const handleCourierPackageInfo = (data: { package: CourierPackageInfo | null }): void => {
      setCourierPackageInfo(data.package);
    };

    const handleConsumeBlueprintResult = (data: { success: boolean; message: string; recipeId?: string }): void => {
      setMessage(data.message);
      if (data.success) {
        socket.emit('inventory:get');
        setContextMenu({ visible: false, x: 0, y: 0, item: null });
      }
      setTimeout(() => setMessage(''), 3000);
    };

    console.log('📦 Registering socket listeners, socket.id:', socket.id);
    socket.on('inventory:data', handleInventoryUpdate);
     socket.on('inventory:sell:success', handleSellResult);
     socket.on('inventory:equip:success', handleEquipResult);
     socket.on('inventory:unequip:success', handleEquipResult);
     socket.on('inventory:use:result', handleUseResult);
     socket.on('inventory:error', handleError);
     socket.on('loot:items', handleLootItems);
     socket.on('courier:packageInfo', handleCourierPackageInfo);
     socket.on('crafting:consume_blueprint:result', handleConsumeBlueprintResult);

     return () => {
       socket.off('inventory:data', handleInventoryUpdate);
       socket.off('inventory:sell:success', handleSellResult);
       socket.off('inventory:equip:success', handleEquipResult);
       socket.off('inventory:unequip:success', handleEquipResult);
       socket.off('inventory:use:result', handleUseResult);
       socket.off('inventory:error', handleError);
       socket.off('loot:items', handleLootItems);
       socket.off('courier:packageInfo', handleCourierPackageInfo);
       socket.off('crafting:consume_blueprint:result', handleConsumeBlueprintResult);
    };
  }, [socket, onInventoryUpdate]);
  
  // Callback hooks must be before early return
  const handleContextMenu = useCallback((e: React.MouseEvent, item: InventoryItem): void => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clamp context menu position to viewport
    const menuWidth = 150;
    const menuHeight = 200;
    let x = e.clientX;
    let y = e.clientY;
    
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 8;
    }
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 8;
    }
    
    setContextMenu({
      visible: true,
      x,
      y,
      item,
    });
  }, []);
  
  const handleMouseEnter = useCallback((e: React.MouseEvent, item: InventoryItem): void => {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    
    // Default position to the right of the slot with offset from mouse
    let nextX: number = rect.right + 8;
    let nextY: number = rect.bottom + 4;

    const additionalInfo = isCourierPackage(item.itemId) && courierPackageInfo ? (
      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #4a4a6a' }}>
        <div style={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>
          DELIVERY DESTINATION
        </div>
        <div style={{ color: '#aaf', fontSize: '12px' }}>
          {courierPackageInfo.destinationLocationName}
        </div>
        <div style={{ color: '#ffd700', fontSize: '11px', marginTop: '4px' }}>
          Reward: {courierPackageInfo.rewardPieces.toLocaleString()}₱
        </div>
        {courierPackageInfo.timeLimitExpiresAt && (
          <div style={{ 
            color: courierPackageInfo.timeLimitExpiresAt - Date.now() < 300000 ? '#f88' : '#fa8', 
            fontSize: '11px', 
            marginTop: '2px' 
          }}>
            {courierPackageInfo.timeLimitExpiresAt - Date.now() > 0 
              ? `Time: ${Math.floor((courierPackageInfo.timeLimitExpiresAt - Date.now()) / 60000)}m remaining`
              : 'EXPIRED'}
          </div>
        )}
      </div>
    ) : null;

    showTooltip(
      {
        title: item.definition?.name || 'Unknown Item',
        type: item.definition?.type || 'item',
        quantity: item.quantity,
        rarity: item.definition?.rarity,
        description: item.definition?.description,
        stats: item.definition?.stats,
        additionalInfo,
      },
      nextX,
      nextY
    );
    
    // Request courier package info if this is a courier item
    if (isCourierPackage(item.itemId) && socket) {
      socket.emit('courier:getPackageInfo', { itemId: item.itemId });
    } else {
      setCourierPackageInfo(null);
    }
  }, [socket, showTooltip, courierPackageInfo]);
  
  const handleMouseLeave = useCallback((): void => {
    hideTooltip();
  }, [hideTooltip]);

  if (!isOpen) return null;

  const handleEquip = (itemId: string): void => {
    socket?.emit('inventory:equip', { itemId });
    setSelectedItem(null);
  };

  const handleUnequip = (slot: string): void => {
    socket?.emit('inventory:unequip', { slot });
  };
  
  const handleDiscard = (itemId: string): void => {
    socket?.emit('inventory:discard', { itemId });
    setContextMenu({ visible: false, x: 0, y: 0, item: null });
    setSelectedItem(null);
  };

  const handleDragStart = (e: React.DragEvent, item: InventoryItem, index: number): void => {
    setDraggedItem(item);
    e.dataTransfer.setData('text/plain', JSON.stringify({ itemId: item.itemId, fromIndex: index }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (): void => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, toIndex: number): void => {
    e.preventDefault();
    setDragOverIndex(null);
    setDraggedItem(null);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      const fromIndex = data.fromIndex;
      
      if (fromIndex !== toIndex && fromIndex !== undefined) {
        socket?.emit('inventory:swap', { fromIndex, toIndex });
      }
    } catch {
      // Invalid drag data
    }
  };

  const handleDragEnd = (): void => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const isEssenceItem = (itemId: string): boolean => itemId.startsWith('essence_');
  
  const getEssenceColor = (itemId: string): string => {
    const essenceRarities: Record<string, string> = {
      'essence_flame': '#ff6b35',
      'essence_storm': '#00bfff',
      'essence_tide': '#1e90ff',
      'essence_shadow': '#4b0082',
      'essence_iron': '#708090',
      'essence_spirit': '#9932cc',
      'essence_inferno': '#ff2200',
      'essence_glacier': '#87ceeb',
      'essence_dragon': '#ff4500',
      'essence_void_king': '#000000',
    };
    return essenceRarities[itemId] || '#666';
  };

  const renderItemSlot = (item: InventoryItem, index: number): JSX.Element => {
    const def = item.definition;
    const isEssence = isEssenceItem(item.itemId);
    const rarityColor = isEssence ? getEssenceColor(item.itemId) : (def ? RARITY_COLORS[def.rarity] : '#666');
    const isDragOver = dragOverIndex === index;
    const isDragging = draggedItem?.itemId === item.itemId;
    
    // Always render with icon, even if definition is missing
    const itemName = def?.name || item.itemId.replace(/_/g, ' ').toUpperCase();
    const itemIcon = def?.icon || undefined;
    
    return (
      <div
        key={item.itemId}
        className={'inventory-slot' + (selectedItem?.itemId === item.itemId ? ' selected' : '') + (isDragOver ? ' drag-over' : '') + (isDragging ? ' dragging' : '') + (isEssence ? ' essence-item' : '')}
        style={{ 
          borderColor: isDragOver ? '#3498db' : rarityColor,
          boxShadow: isEssence ? `0 0 12px ${rarityColor}80, inset 0 0 8px ${rarityColor}40` : 'none',
          borderWidth: isEssence ? '2px' : '1px',
        }}
        draggable
        onDragStart={(e) => handleDragStart(e, item, index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, index)}
        onDragEnd={handleDragEnd}
        onClick={() => setSelectedItem(item)}
        onContextMenu={(e) => handleContextMenu(e, item)}
        onMouseEnter={(e) => handleMouseEnter(e, item)}
        onMouseLeave={handleMouseLeave}
      >
        <ItemIcon icon={itemIcon} name={itemName} size={32} className="item-img item-img-scaled" itemId={item.itemId} />
        <span className="item-quantity">{item.quantity}</span>
        {isEssence && <div className="essence-indicator" style={{ background: `radial-gradient(circle, ${rarityColor}80, transparent)` }} />}
      </div>
    );
  };

  const renderEquipSlot = (slot: keyof Equipment): JSX.Element => {
    const item = equipment[slot];
    const rarityColor = item ? RARITY_COLORS[item.rarity] : '#30363d';
    const slotLabel = SLOT_LABELS[slot];
    
    return (
      <div
        className={`equip-slot-container${item ? ' equipped' : ''}`}
        onClick={() => item && handleUnequip(slot)}
        title={item ? `${item.name} (click to unequip)` : `Empty ${slotLabel} slot`}
      >
        <div className="equip-slot-label">{slotLabel}</div>
        <div
          className="equip-slot"
          style={{ borderColor: rarityColor }}
        >
          {item ? (
            renderItemIcon(item.icon, item.id, item.name, 'item-img')
          ) : (
            <span className="empty-slot">-</span>
          )}
        </div>
        {item && item.stats && (
          <div className="equip-stats">
            {Object.entries(item.stats).map(([key, value]) => (
              <span key={key} className="equip-stat">
                {STAT_LABELS[key] || key}: +{value}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="inventory-panel"
      ref={panelRef}
      style={{
        position: 'fixed',
        zIndex: 1000,
        width: '500px',
        maxHeight: '80vh',
        backgroundColor: '#0d1117',
        border: '1px solid #30363d',
        boxShadow: '0 0 20px rgba(0,0,0,0.8)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        left: '50px',
        top: '50px',
        ...dragStyle,
      }}
    >
        {/* Header */}
        <div
          className="inventory-header"
          onMouseDown={handleHeaderMouseDown}
          style={{ cursor: 'grab', userSelect: 'none' }}
        >
          <h2 className="font-lookout-24">Inventory</h2>
           <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {hasEssence && (
                <button
                  onClick={() => onOpenWildcardPowers?.()}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#a371f7',
                    color: '#ffffff',
                    border: '1px solid #30363d',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    fontFamily: '"Sometype Mono", monospace',
                    transition: 'background-color 0.2s, border-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#bc8ef7';
                    (e.currentTarget as HTMLButtonElement).style.borderColor = '#d2a8ff';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#a371f7';
                    (e.currentTarget as HTMLButtonElement).style.borderColor = '#30363d';
                  }}
                  title="Manage signature moves"
                >
                  SIGNATURE MOVES
                </button>
              )}
             <button
               onClick={() => {
                 const event = new CustomEvent('cooking:open');
                 window.dispatchEvent(event);
               }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#238636',
                  color: '#ffffff',
                  border: '1px solid #30363d',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 0.2s, border-color 0.2s',
                }}
               onMouseEnter={(e) => {
                 (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#2ea043';
                 (e.currentTarget as HTMLButtonElement).style.borderColor = '#58a6ff';
               }}
               onMouseLeave={(e) => {
                 (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#238636';
                 (e.currentTarget as HTMLButtonElement).style.borderColor = '#30363d';
               }}
               title="Open cooking interface"
             >
               COOKING
              </button>
              <button
                onClick={() => {
                  const event = new CustomEvent('smithing:open');
                  window.dispatchEvent(event);
                }}
                 style={{
                   padding: '6px 12px',
                   backgroundColor: '#8b6914',
                   color: '#ffffff',
                   border: '1px solid #30363d',
                   cursor: 'pointer',
                   fontSize: '11px',
                   fontWeight: 'bold',
                   fontFamily: '"Sometype Mono", monospace',
                   transition: 'background-color 0.2s, border-color 0.2s',
                 }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#b8860b';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = '#d4af37';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#8b6914';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = '#30363d';
                }}
                title="Open smithing interface"
              >
               SMITHING
              </button>
              <button
                onClick={() => {
                  const event = new CustomEvent('alchemy:open');
                  window.dispatchEvent(event);
                }}
                 style={{
                   padding: '6px 12px',
                   backgroundColor: '#6b2c91',
                   color: '#ffffff',
                   border: '1px solid #30363d',
                   cursor: 'pointer',
                   fontSize: '11px',
                   fontWeight: 'bold',
                   fontFamily: '"Sometype Mono", monospace',
                   transition: 'background-color 0.2s, border-color 0.2s',
                 }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#8b3fb3';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = '#b369d9';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#6b2c91';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = '#30363d';
                 }}
                 title="Open alchemy interface"
               >
                 ALCHEMY
                </button>
              <span className="pieces-count">{pieces.toLocaleString()}₱</span>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>

          {/* Message */}
          {message && <div className="inventory-message">{message}</div>}

          {/* Content */}
          <div className="inventory-content">
          {/* Equipment */}
          <div className="equipment-section">
            <h3>Equipment</h3>
            <div className="equipment-slots">
              {isLoading ? (
                <>
                  <EquipSlotSkeleton label="Weapon" />
                  <EquipSlotSkeleton label="Armor" />
                  <EquipSlotSkeleton label="Accessory" />
                  <EquipSlotSkeleton label="Accessory" />
                </>
              ) : (
                <>
                  {renderEquipSlot('weapon')}
                  {renderEquipSlot('armor')}
                  {renderEquipSlot('accessory1')}
                  {renderEquipSlot('accessory2')}
                </>
              )}
            </div>
          </div>

          {/* Items Grid */}
          <div className="items-section">
            <h3>Items {isLoading ? '' : `(${items.length}/24)`}</h3>
            <div className="items-grid">
              {isLoading ? (
                // Show skeleton slots while loading
                Array.from({ length: 12 }).map((_, i) => (
                  <SlotSkeleton key={'skeleton-' + i} />
                ))
              ) : (
                <>
                  {items.map((item, index) => renderItemSlot(item, index))}
                  {Array.from({ length: Math.max(0, 24 - items.length) }).map((_, i) => (
                    <div key={'empty-' + i} className="inventory-slot empty" />
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Selected Item Details - Renovated Bottom Panel */}
          {selectedItem && selectedItem.definition && (
            <div className="item-details-panel">
              <div className="item-details-header">
                {renderItemIcon(selectedItem.definition.icon, selectedItem.itemId, selectedItem.definition.name, 'detail-icon-img')}
                <div className="item-details-info">
                  <span
                    className="item-details-name"
                    style={{ color: RARITY_COLORS[selectedItem.definition.rarity] }}
                  >
                    {selectedItem.definition.name}
                  </span>
                  <span 
                    className="item-details-rarity"
                    style={{ color: RARITY_COLORS[selectedItem.definition.rarity] }}
                  >
                    {RARITY_LABELS[selectedItem.definition.rarity]} {selectedItem.definition.type}
                  </span>
                </div>
              </div>
              <p className="item-details-desc">{selectedItem.definition.description}</p>
              {selectedItem.definition.stats && Object.keys(selectedItem.definition.stats).length > 0 && (
                <div className="item-details-stats">
                  {Object.entries(selectedItem.definition.stats).map(([key, value]) => (
                    <span key={key} className="item-stat-badge" style={{ backgroundColor: value > 0 ? '#1a472a' : '#4a1a1a', borderColor: value > 0 ? '#2ecc71' : '#e74c3c' }}>
                      {STAT_LABELS[key] || key}: {value > 0 ? '+' : ''}{value}
                    </span>
                  ))}
                </div>
              )}
              {selectedItem.quantity > 1 && (
                <div className="item-details-quantity">Quantity: {selectedItem.quantity}</div>
              )}
              <div className="item-details-actions">
                {selectedItem.definition.equipSlot && (
                  <button className="action-btn equip-btn" onClick={() => handleEquip(selectedItem.itemId)}>Equip</button>
                )}
                {selectedItem.definition.type === 'consumable' && (
                  <button className="action-btn use-btn" onClick={() => socket?.emit('inventory:use', { itemId: selectedItem.itemId })}>Use</button>
                )}
                {selectedItem.definition.type === 'blueprint' && (() => {
                  const blueprint = selectedItem.definition as any;
                  const isAlreadyUnlocked = blueprint.recipeId && (window as any).__unlockedBlueprints?.has(blueprint.recipeId);
                  return (
                    <button 
                      className="action-btn use-btn" 
                      onClick={() => socket?.emit('crafting:consume_blueprint', { blueprintId: selectedItem.itemId })}
                      disabled={isAlreadyUnlocked}
                      style={{ opacity: isAlreadyUnlocked ? 0.5 : 1, cursor: isAlreadyUnlocked ? 'not-allowed' : 'pointer' }}
                      title={isAlreadyUnlocked ? 'Recipe already unlocked' : 'Consume blueprint to unlock recipe'}
                    >
                      {isAlreadyUnlocked ? 'Already Unlocked' : 'Consume'}
                    </button>
                  );
                })()}
                {selectedItem.itemId.startsWith('construction_kit_') && (
                  <button 
                    className="action-btn use-btn" 
                    onClick={() => {
                      // Emit event to enter construction mode
                      const event = new CustomEvent('construction:enter_mode', {
                        detail: { 
                          kitItemId: selectedItem.itemId, 
                          kitName: selectedItem.definition?.name || 'Construction Kit' 
                        }
                      });
                      window.dispatchEvent(event);
                      // Close the inventory panel
                      onClose();
                    }}
                    style={{ backgroundColor: '#8b4513', borderColor: '#954e1bff', color: '#f0f6fc'}}
                    title="Enter construction mode to place tiles in the world"
                  >
                    Place Tile
                  </button>
                )}
                <button className="action-btn discard-btn" onClick={() => handleDiscard(selectedItem.itemId)}>Discard</button>
              </div>
            </div>
          )}
        </div>
        
        {/* Context Menu */}
        {contextMenu.visible && contextMenu.item && (
          <div
            className="inventory-context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            {contextMenu.item.definition?.equipSlot && (
              <button onClick={() => { handleEquip(contextMenu.item!.itemId); setContextMenu({ visible: false, x: 0, y: 0, item: null }); }}>
                Equip
              </button>
            )}
            {contextMenu.item.definition?.type === 'consumable' && contextMenu.item.itemId.startsWith('essence_') && (
              <button onClick={() => { 
                const essenceMap: Record<string, { name: string; description: string; rarity: 'rare' | 'epic' | 'legendary'; unlocksMove: string }> = {
                  'essence_flame': { name: 'Ember Essence', description: 'A crystallized fragment of pure flame. Awakens fire within.', rarity: 'rare', unlocksMove: 'flame_burst' },
                  'essence_storm': { name: 'Tempest Essence', description: 'Captured lightning from a violent storm. Power over wind and thunder.', rarity: 'rare', unlocksMove: 'storm_strike' },
                  'essence_tide': { name: 'Abyssal Essence', description: 'Water from the deepest trenches. Crushing pressure of the sea.', rarity: 'rare', unlocksMove: 'tidal_wave' },
                  'essence_shadow': { name: 'Void Essence', description: 'Darkness given form. Mastery over shadows and stealth.', rarity: 'rare', unlocksMove: 'shadow_step' },
                  'essence_iron': { name: 'Titan Essence', description: 'Metal infused with ancient power. Hardens the body like steel.', rarity: 'rare', unlocksMove: 'iron_body' },
                  'essence_spirit': { name: 'Sovereign Essence', description: 'Pure willpower crystallized. Grants an aura of dominance.', rarity: 'epic', unlocksMove: 'spirit_pressure' },
                  'essence_inferno': { name: 'Inferno Essence', description: 'Heart of a dying star. Devastating flame unleashed.', rarity: 'epic', unlocksMove: 'inferno_cannon' },
                  'essence_glacier': { name: 'Glacier Essence', description: 'Ice from frozen reaches. Commands absolute zero.', rarity: 'epic', unlocksMove: 'frozen_domain' },
                  'essence_dragon': { name: 'Dragon Essence', description: 'Breath of an ancient sea dragon. Legendary power.', rarity: 'legendary', unlocksMove: 'dragon_breath' },
                  'essence_void_king': { name: 'Void King Essence', description: 'Darkness from beyond the world. Absolute shadow mastery.', rarity: 'legendary', unlocksMove: 'void_realm' },
                };
                const essenceData = essenceMap[contextMenu.item!.itemId];
                if (essenceData) {
                  setEssenceConsumption({ 
                    isOpen: true, 
                    itemId: contextMenu.item!.itemId, 
                    name: essenceData.name,
                    description: essenceData.description,
                    rarity: essenceData.rarity,
                    unlocksMove: essenceData.unlocksMove,
                    moveDefinition: null
                  });
                }
                setContextMenu({ visible: false, x: 0, y: 0, item: null }); 
              }}>
                Unlock Move
              </button>
            )}
            {contextMenu.item.definition?.type === 'consumable' && !contextMenu.item.itemId.startsWith('essence_') && (
              <button onClick={() => { socket?.emit('inventory:use', { itemId: contextMenu.item!.itemId }); setContextMenu({ visible: false, x: 0, y: 0, item: null }); }}>
                Use
              </button>
            )}
            {contextMenu.item.definition?.type === 'blueprint' && (
              <button onClick={() => { 
                socket?.emit('crafting:consume_blueprint', { blueprintId: contextMenu.item!.itemId }); 
                setContextMenu({ visible: false, x: 0, y: 0, item: null }); 
              }}>
                Consume Blueprint
              </button>
            )}
            <button className="danger" onClick={() => handleDiscard(contextMenu.item!.itemId)}>
              Discard
            </button>
          </div>
        )}
      
      {/* Essence Consumption Modal */}
      {essenceConsumption?.isOpen && (
        <EssenceConsumption
          essenceItemId={essenceConsumption.itemId}
          essenceName={essenceConsumption.name}
          essenceDescription={essenceConsumption.description}
          essenceRarity={essenceConsumption.rarity}
          unlocksMove={essenceConsumption.unlocksMove}
          moveDefinition={essenceConsumption.moveDefinition}
          socket={socket}
          onClose={() => setEssenceConsumption(null)}
          onSuccess={() => {
            setEssenceConsumption(null);
            socket?.emit('inventory:get');
          }}
        />
      )}

      </div>
  );
};
